import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Calculator, 
  CreditCard, 
  Shield, 
  Clock, 
  CheckCircle,
  Info,
  ExternalLink,
  Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FinancePage = () => {
  const [projectCost, setProjectCost] = useState(5000);
  const [term, setTerm] = useState(36);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [financeConfig, setFinanceConfig] = useState({
    providerUrl: null,
    isConfigured: false
  });

  // APR for calculation (example rate)
  const APR = 9.9;

  useEffect(() => {
    // Fetch finance URL configuration from backend
    const fetchFinanceConfig = async () => {
      try {
        const response = await axios.get(`${API}/settings/finance-url`);
        setFinanceConfig({
          providerUrl: response.data.finance_url,
          isConfigured: response.data.is_configured
        });
      } catch (error) {
        console.error("Error fetching finance config:", error);
        // Keep default values if fetch fails
      }
    };
    fetchFinanceConfig();
  }, []);

  useEffect(() => {
    // Calculate monthly payment using standard loan formula
    const monthlyRate = APR / 100 / 12;
    const payment = (projectCost * monthlyRate * Math.pow(1 + monthlyRate, term)) / 
                    (Math.pow(1 + monthlyRate, term) - 1);
    setMonthlyPayment(Math.round(payment * 100) / 100);
  }, [projectCost, term]);

  const termOptions = [
    { value: 12, label: "12 months" },
    { value: 24, label: "24 months" },
    { value: 36, label: "36 months" },
    { value: 48, label: "48 months" },
    { value: 60, label: "60 months" }
  ];

  const benefits = [
    { icon: CreditCard, title: "Spread the Cost", description: "Pay in manageable monthly instalments" },
    { icon: Clock, title: "Quick Decision", description: "Get a decision in minutes" },
    { icon: Shield, title: "Regulated Provider", description: "Finance provided by FCA regulated lenders" },
    { icon: CheckCircle, title: "No Upfront Cost", description: "Start your project without paying everything upfront" }
  ];

  const exampleProjects = [
    { name: "Kitchen Splashback", range: "£500 - £1,500", monthly: "from £15/month" },
    { name: "Bathroom Tiling", range: "£1,500 - £4,000", monthly: "from £45/month" },
    { name: "Bathroom Refit", range: "£3,500 - £12,000", monthly: "from £105/month" },
    { name: "Floor Tiling Project", range: "£2,000 - £8,000", monthly: "from £60/month" }
  ];

  const handleApplyFinance = () => {
    if (financeConfig.isConfigured && financeConfig.providerUrl) {
      window.open(financeConfig.providerUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div data-testid="finance-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50 grid-pattern">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <CreditCard className="w-4 h-4" />
              <span className="text-sm font-medium">Flexible Finance Available</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-montserrat">
              Spread the Cost of Your Bathroom or Tiling Project
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8">
              Flexible finance options allow you to complete your renovation now and pay in monthly instalments. Example project values from £2,000 to £15,000.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#calculator">
                <Button size="lg" className="bg-primary hover:bg-primary/90" data-testid="finance-cta-calculator">
                  <Calculator className="w-5 h-5 mr-2" />
                  Calculate Monthly Payments
                </Button>
              </a>
              {financeConfig.isConfigured ? (
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-primary text-primary hover:bg-primary/5"
                  onClick={handleApplyFinance}
                  data-testid="finance-cta-apply"
                >
                  Apply For Finance
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Finance Calculator */}
      <section id="calculator" className="section-padding" data-testid="finance-calculator-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
                Finance Calculator
              </h2>
              <p className="text-lg text-gray-600">
                See how affordable your project could be with monthly payments
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              {/* Project Cost Slider */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-base font-medium">Project Cost</Label>
                  <span className="text-2xl font-bold text-primary">£{projectCost.toLocaleString()}</span>
                </div>
                <Slider
                  value={[projectCost]}
                  onValueChange={(value) => setProjectCost(value[0])}
                  min={500}
                  max={20000}
                  step={100}
                  className="mb-2"
                  data-testid="finance-cost-slider"
                />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>£500</span>
                  <span>£20,000</span>
                </div>
              </div>

              {/* Term Selection */}
              <div className="mb-8">
                <Label className="text-base font-medium mb-4 block">Finance Term</Label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {termOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setTerm(option.value)}
                      className={`p-3 rounded-xl border-2 transition-all text-sm ${
                        term === option.value
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-gray-200 hover:border-primary/50"
                      }`}
                      data-testid={`term-${option.value}`}
                    >
                      <span className="font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Monthly Payment Result */}
              <div className="bg-gradient-to-br from-primary to-blue-700 rounded-xl p-8 text-center text-white mb-6">
                <p className="text-white/80 mb-2">Estimated Monthly Payment</p>
                <p className="text-5xl font-bold mb-2" data-testid="monthly-payment">
                  £{monthlyPayment.toLocaleString()}
                </p>
                <p className="text-white/70 text-sm">per month for {term} months</p>
                <p className="text-white/60 text-xs mt-2">
                  Representative {APR}% APR (example rate)
                </p>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-500">Project Cost</p>
                    <p className="font-bold text-gray-900">£{projectCost.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Payable</p>
                    <p className="font-bold text-gray-900">£{Math.round(monthlyPayment * term).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Finance Term</p>
                    <p className="font-bold text-gray-900">{term} months</p>
                  </div>
                </div>
              </div>

              {/* Apply Button or Lender Links */}
              {financeConfig.isConfigured ? (
                <Button 
                  className="w-full bg-primary hover:bg-primary/90 py-6 text-lg"
                  onClick={handleApplyFinance}
                  data-testid="calculator-apply-button"
                >
                  Apply For Finance
                  <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 text-center mb-3">Apply directly with our finance partners:</p>
                  <a 
                    href="https://www.kanda.co.uk" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    <span className="font-semibold text-gray-900">Kanda</span>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </a>
                  <a 
                    href="https://www.hitachicapital.co.uk" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    <span className="font-semibold text-red-600">Hitachi Personal Finance</span>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </a>
                  <a 
                    href="https://www.v12retailfinance.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    <span className="font-semibold text-blue-600">V12 Retail Finance</span>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </a>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Or call us on <a href="tel:+447547393455" className="text-primary font-medium">07547 393 455</a> to discuss options
                  </p>
                </div>
              )}

              {/* Disclaimer */}
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Finance subject to status and approval. Terms and conditions apply. 
                    Finance is provided by a regulated third-party lender. This calculator 
                    provides estimates only and actual rates may vary based on your circumstances.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Example Projects */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
              Example Project Costs
            </h2>
            <p className="text-lg text-gray-600">
              Typical project values from £2,000 to £15,000
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {exampleProjects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-2">{project.name}</h3>
                <p className="text-2xl font-bold text-primary mb-1">{project.range}</p>
                <p className="text-sm text-gray-500">{project.monthly}*</p>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            *Based on {APR}% APR over 36 months. Representative example only.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
              How Finance Works
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {[
                { step: 1, title: "Get Your Quote", description: "Use our estimator tools to get a ballpark price for your project." },
                { step: 2, title: "Check Your Payments", description: "Use our calculator to see estimated monthly payments." },
                { step: 3, title: "Apply Online", description: "Complete a quick online application with our finance partner." },
                { step: 4, title: "Get a Decision", description: "Receive a decision, often within minutes." },
                { step: 5, title: "Start Your Project", description: "Once approved, we can begin your tiling or bathroom renovation." }
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Finance Providers */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
              Our Finance Partners
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We work with trusted, FCA-regulated finance providers to offer flexible payment options
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Kanda */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="h-16 flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-gray-900">Kanda</span>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Leading UK home improvement finance provider with simple online applications.
                </p>
                <ul className="text-xs text-gray-500 space-y-1 mb-4">
                  <li>✓ Instant decisions</li>
                  <li>✓ Flexible terms 1-10 years</li>
                  <li>✓ 0% deposit available</li>
                </ul>
                <a 
                  href="https://www.kanda.co.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary text-sm font-medium hover:underline"
                >
                  Learn more <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Hitachi Personal Finance */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="h-16 flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-red-600">Hitachi</span>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  One of the UK's largest consumer finance providers, trusted by millions.
                </p>
                <ul className="text-xs text-gray-500 space-y-1 mb-4">
                  <li>✓ Established since 1982</li>
                  <li>✓ Competitive rates</li>
                  <li>✓ Trusted UK lender</li>
                </ul>
                <a 
                  href="https://www.hitachicapital.co.uk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary text-sm font-medium hover:underline"
                >
                  Learn more <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* V12 Retail Finance */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="h-16 flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-blue-600">V12 Finance</span>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Specialist home improvement finance with quick approvals.
                </p>
                <ul className="text-xs text-gray-500 space-y-1 mb-4">
                  <li>✓ Apply online in minutes</li>
                  <li>✓ Up to £50,000 available</li>
                  <li>✓ 24-120 month terms</li>
                </ul>
                <a 
                  href="https://www.v12retailfinance.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary text-sm font-medium hover:underline"
                >
                  Learn more <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-gray-500 mt-8">
            We are in the process of setting up accounts with these providers. Contact us to discuss finance options for your project.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-montserrat">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Get a quote for your project and explore your finance options today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button size="lg" className="bg-white text-primary hover:bg-gray-100" data-testid="finance-quote-cta">
                Get Tiling Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/bathroom-refit">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                Bathroom Refit Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer Disclaimer */}
      <section className="py-8 bg-gray-100">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm text-gray-600">
              <strong>Finance Disclaimer:</strong> Finance is subject to status and approval. 
              Terms and conditions apply. Finance is provided by a regulated third-party lender. 
              DC Tiling acts as a credit intermediary and not a lender. Credit is subject to 
              application and status. Written quotations available on request.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancePage;
